import { useMutation } from "@apollo/client";
import { GoalScope, GoalState } from "types/graphql-schema";

import getArtifactActivitiesQuery from "@apps/artifact-sidebar/graphql/get-artifact-activities-query";
import getDashboardGoalsQuery from "@apps/dashboard-new/graphql/get-dashboard-goals-query";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import { buttonTheme } from "@components/button/button-constants";
import GoalIcon from "@components/goal-icon/goal-icon";
import { useLink } from "@components/link/link-helpers";
import DeprecatedSelect, { SelectOption } from "@components/select/select";
import { classNames, getGoalTextColorClassName } from "@helpers/css";

import updateArtifactMutation from "../graphql/update-artifact-mutation";

export const GoalScopeDropdownInput = ({
  artifact,
  onChangeScope,
}: {
  artifact: {
    scope: GoalScope;
    state: GoalState;
    canUpdate: { permission: boolean };
  };
  onChangeScope: (scope: SelectOption<GoalScope>) => void;
}) => {
  const currentUser = currentUserVar();
  const label = useLabel();
  return (
    <DeprecatedSelect<GoalScope>
      disabled={!artifact.canUpdate.permission}
      buttonProps={{
        theme: buttonTheme.text,
        "aria-label": "Goal scope dropdown button",
      }}
      options={[
        {
          value: GoalScope.Personal,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Personal}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Personal),
                  hovered && "text-white",
                )}
              >
                {label("goal", { titleCase: true })}
              </span>
            </span>
          ),
        },
        {
          value: GoalScope.Team,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Team}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Team),
                  hovered && "text-white",
                )}
              >
                {label("team", { titleCase: true })} {label("goal")}
              </span>
            </span>
          ),
          description: !currentUser.paidFeatures.canCreateGroupGoals
            ? "Upgrade to a higher tier"
            : "",
        },
        {
          value: GoalScope.Organization,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Organization}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Organization),
                  hovered && "text-white",
                )}
              >
                {label("organization", { titleCase: true })} {label("goal")}
              </span>
            </span>
          ),
          description: !currentUser.paidFeatures.canCreateGroupGoals
            ? "Upgrade to a higher tier"
            : "",
        },
        {
          value: GoalScope.Development,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Development}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Development),
                  hovered && "text-white",
                )}
              >
                {label("development", { capitalize: true })} {label("goal")}
              </span>
            </span>
          ),
          description: !currentUser.paidFeatures.canCreateGroupGoals
            ? "Upgrade to a higher tier"
            : "",
        },
      ]}
      value={artifact.scope}
      onChange={onChangeScope}
    />
  );
};

const GoalScopeDropdown = ({
  artifact,
}: {
  artifact: {
    id: number;
    scope: GoalScope;
    state: GoalState;
    canUpdate: { permission: boolean };
  };
}) => {
  const link = useLink();
  const currentUser = currentUserVar();
  const [updateArtifact] = useMutation(updateArtifactMutation);

  const handleChangeScope = (option: SelectOption<GoalScope>) => {
    if (
      !currentUser.paidFeatures.canCreateGroupGoals &&
      option.value !== GoalScope.Personal
    ) {
      return link.redirect("/billing");
    }
    updateArtifact({
      variables: {
        artifactId: artifact.id,
        additionalFields: { goalScope: option.value },
      },
      optimisticResponse: {
        createOrUpdateArtifact: {
          artifact: {
            ...artifact,
            scope: option.value,
          },
          __typename: "CreateOrUpdateArtifactMutation",
        },
      },
      refetchQueries: [getDashboardGoalsQuery, getArtifactActivitiesQuery],
    });
  };

  return (
    <GoalScopeDropdownInput
      artifact={artifact}
      onChangeScope={handleChangeScope}
    />
  );
};

export default GoalScopeDropdown;
